/**
 * Turns a product card's "+" add-to-cart button into a quantity stepper
 * once the product is actually in the cart. Direct vanilla-JS port of
 * blocks/src/shared/product-card-quantity.js (ES module, shared by
 * gws/product-card and gws/product-template-card's own view.js entries) —
 * the logic itself has no Gutenberg/block-editor dependency at all (plain
 * DOM class-name selectors, fetch(), optional window.jQuery), so this is
 * a near-verbatim copy wrapped in an IIFE and exposed as
 * window.GWSInitProductCardQuantity for reuse by both the product-card
 * and product-template-card Elementor widgets, same pattern as
 * window.GWSInitCardSizer in elementor-card-sizer.js.
 *
 * The initial add still goes through WooCommerce's own ajax_add_to_cart
 * (core's add-to-cart.js, bound via the .ajax_add_to_cart.add_to_cart_button
 * classes GWS_Render_Product_Card_Markup() already outputs) - this only
 * takes over once that's confirmed, via WooCommerce's own `added_to_cart`
 * jQuery event plus a page-load /gws/v1/cart check (for products already
 * in the cart from a previous visit), and handles subsequent +/- clicks
 * through the custom /gws/v1/cart/quantity REST route
 * (gwschild/inc/woocommerce.php — unchanged, reused as-is).
 *
 * Known limitation (same as the original): if the same product appears in
 * more than one card on the same page, a +/- click on one stepper only
 * updates that instance - the others reconcile on next page load.
 */
( function () {
	'use strict';

	var FALLBACK_BIN_ICON =
		'<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
		'<path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6h16Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
		'</svg>';

	// product_id (string) => original "+" button outerHTML, so a stepper
	// can revert to the exact PHP-rendered button once quantity reaches 0.
	var originalButtons = {};

	function cartSettings() {
		return window.gwsCart || null;
	}

	function applyFragments( fragments ) {
		if ( ! fragments ) {
			return;
		}

		Object.keys( fragments ).forEach( function ( selector ) {
			document.querySelectorAll( selector ).forEach( function ( el ) {
				el.outerHTML = fragments[ selector ];
			} );
		} );
	}

	function updateStepperDisplay( stepper, quantity ) {
		var valueEl = stepper.querySelector( '.gws-product-card__qty-value' );
		if ( valueEl ) {
			valueEl.textContent = quantity;
		}

		var minusBtn = stepper.querySelector( '.gws-product-card__qty-minus' );
		if ( ! minusBtn ) {
			return;
		}

		if ( 1 === quantity ) {
			var settings = cartSettings();
			minusBtn.innerHTML = ( settings && settings.binIcon ) || FALLBACK_BIN_ICON;
			minusBtn.classList.add( 'is-remove' );
			minusBtn.setAttribute( 'aria-label', 'Remove from cart' );
		} else {
			minusBtn.textContent = '−';
			minusBtn.classList.remove( 'is-remove' );
			minusBtn.setAttribute( 'aria-label', 'Remove one' );
		}
	}

	function postQuantity( productId, action ) {
		var config = cartSettings();
		if ( ! config ) {
			return Promise.resolve( null );
		}

		return fetch( config.restUrl + 'cart/quantity', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': config.nonce,
			},
			body: JSON.stringify( { product_id: productId, action: action } ),
		} )
			.then( function ( response ) { return response.ok ? response.json() : null; } )
			.catch( function () { return null; } );
	}

	function reconcileQuantity( stepper, productId ) {
		var config = cartSettings();
		if ( ! config ) {
			return;
		}

		// X-WP-Nonce required here too, not just on the POST below - a
		// logged-in tester's browser has valid wp-admin auth cookies, and
		// WordPress's cookie-auth REST check (rest_cookie_check_errors())
		// rejects ANY REST request from such a browser that's missing a
		// valid nonce, GET included. Without it this silently 403'd and
		// the stepper never picked up the real cart quantity on load.
		fetch( config.restUrl + 'cart', { headers: { 'X-WP-Nonce': config.nonce } } )
			.then( function ( response ) { return response.ok ? response.json() : null; } )
			.then( function ( cart ) {
				if ( cart && cart[ productId ] ) {
					updateStepperDisplay( stepper, cart[ productId ] );
				}
			} )
			.catch( function () {} );
	}

	function revertToAddButton( stepper, productId ) {
		var original = originalButtons[ String( productId ) ];

		if ( ! original ) {
			stepper.remove();
			return;
		}

		var template = document.createElement( 'template' );
		template.innerHTML = original.trim();
		stepper.replaceWith( template.content.firstElementChild );
	}

	/**
	 * Tells any WooCommerce Blocks cart-aware component on the page (Mini
	 * Cart, Cart, Checkout, All Products) to refetch from the Store API —
	 * our own /gws/v1/cart/quantity route mutates the cart entirely
	 * outside both the classic wc-ajax add-to-cart flow and the Store
	 * API, so neither of WooCommerce Blocks' own two refresh triggers
	 * fires on its own.
	 */
	function notifyBlocksCartChanged() {
		if ( ! window.CustomEvent ) {
			return;
		}

		document.body.dispatchEvent(
			new CustomEvent( 'wc-blocks_added_to_cart', {
				bubbles: true,
				cancelable: true,
				detail: { preserveCartData: false },
			} )
		);
	}

	/**
	 * Tells any OTHER quantity-aware widget on the page (another product
	 * card showing the same product, or the Cart Items widget's stepper —
	 * see elementor-cart-items.js, which dispatches this same event after
	 * its own successful changes) that the cart actually changed, so it
	 * can reconcile itself via syncFromCart() below. Fixes what used to be
	 * a known limitation: a +/- click on one stepper only updated that one
	 * instance, leaving any other card for the same product showing a
	 * stale quantity until the next full page load.
	 */
	function notifyGwsCartUpdated() {
		if ( ! window.CustomEvent ) {
			return;
		}

		document.dispatchEvent( new CustomEvent( 'gws-cart-updated' ) );
	}

	function handleStepperClick( stepper, productId, action ) {
		if ( stepper.classList.contains( 'is-busy' ) ) {
			return;
		}

		stepper.classList.add( 'is-busy' );

		postQuantity( productId, action ).then( function ( result ) {
			stepper.classList.remove( 'is-busy' );

			if ( ! result ) {
				return;
			}

			applyFragments( result.fragments );
			notifyBlocksCartChanged();
			notifyGwsCartUpdated();

			if ( result.removed ) {
				revertToAddButton( stepper, productId );
				return;
			}

			updateStepperDisplay( stepper, result.quantity );
		} );
	}

	function buildStepper( productId, quantity ) {
		var stepper = document.createElement( 'div' );
		stepper.className = 'gws-product-card__qty';
		stepper.setAttribute( 'data-product_id', productId );
		stepper.innerHTML =
			'<button type="button" class="gws-product-card__qty-btn gws-product-card__qty-minus"></button>' +
			'<span class="gws-product-card__qty-value"></span>' +
			'<button type="button" class="gws-product-card__qty-btn gws-product-card__qty-plus" aria-label="Add one more">+</button>';

		updateStepperDisplay( stepper, quantity );

		stepper
			.querySelector( '.gws-product-card__qty-minus' )
			.addEventListener( 'click', function () { handleStepperClick( stepper, productId, 'decrement' ); } );
		stepper
			.querySelector( '.gws-product-card__qty-plus' )
			.addEventListener( 'click', function () { handleStepperClick( stepper, productId, 'increment' ); } );

		return stepper;
	}

	function transformToStepper( button, quantity ) {
		var productId = button.getAttribute( 'data-product_id' );

		if ( ! productId ) {
			return null;
		}

		if ( ! originalButtons[ productId ] ) {
			originalButtons[ productId ] = button.outerHTML;
		}

		var stepper = buildStepper( productId, quantity );
		button.replaceWith( stepper );

		return stepper;
	}

	function findAddButtons( productId ) {
		var selector = productId
			? '.gws-product-card__add[data-product_id="' + productId + '"]'
			: '.gws-product-card__add[data-product_id]';

		return document.querySelectorAll( selector );
	}

	function findSteppers( productId ) {
		var selector = productId
			? '.gws-product-card__qty[data-product_id="' + productId + '"]'
			: '.gws-product-card__qty[data-product_id]';

		return document.querySelectorAll( selector );
	}

	/**
	 * Reconciles every product card on the page against the actual cart —
	 * "+" buttons for products now in the cart become steppers (original
	 * purpose: correct state after a fresh page load); existing steppers
	 * get their displayed quantity corrected, or revert back to a "+"
	 * button entirely if the product's no longer in the cart (added so
	 * this same function can also serve as the 'gws-cart-updated' handler
	 * below — a card's OWN stepper already updates itself immediately on
	 * its own click, but any OTHER card for the same product, or a change
	 * made through the Cart Items widget's stepper elsewhere on the page,
	 * only reaches this card via that event).
	 */
	function syncFromCart() {
		var config = cartSettings();
		if ( ! config ) {
			return;
		}

		// See reconcileQuantity()'s own comment - GET needs the nonce too.
		fetch( config.restUrl + 'cart', { headers: { 'X-WP-Nonce': config.nonce } } )
			.then( function ( response ) { return response.ok ? response.json() : null; } )
			.then( function ( cart ) {
				if ( ! cart ) {
					return;
				}

				findAddButtons().forEach( function ( button ) {
					var productId = button.getAttribute( 'data-product_id' );
					var quantity = cart[ productId ];

					if ( quantity > 0 ) {
						transformToStepper( button, quantity );
					}
				} );

				findSteppers().forEach( function ( stepper ) {
					var productId = stepper.getAttribute( 'data-product_id' );
					var quantity = cart[ productId ] || 0;

					if ( quantity > 0 ) {
						updateStepperDisplay( stepper, quantity );
					} else {
						revertToAddButton( stepper, productId );
					}
				} );
			} )
			.catch( function () {} );
	}

	function listenForAddedToCart() {
		if ( ! window.jQuery ) {
			return;
		}

		window.jQuery( document.body ).on( 'added_to_cart', function ( event, fragments, cartHash, $button ) {
			applyFragments( fragments );

			var clicked = $button && $button.length ? $button.get( 0 ) : null;

			if ( ! clicked || ! clicked.classList || ! clicked.classList.contains( 'gws-product-card__add' ) ) {
				return;
			}

			var productId = clicked.getAttribute( 'data-product_id' );
			var assumedQuantity = parseInt( clicked.getAttribute( 'data-quantity' ), 10 ) || 1;

			findAddButtons( productId ).forEach( function ( button ) {
				var stepper = transformToStepper( button, assumedQuantity );

				if ( stepper ) {
					reconcileQuantity( stepper, productId );
				}
			} );
		} );
	}

	var initialized = false;

	window.GWSInitProductCardQuantity = function () {
		if ( initialized ) {
			return;
		}
		initialized = true;

		syncFromCart();
		listenForAddedToCart();

		// See notifyGwsCartUpdated()'s docblock — reconciles every card on
		// the page whenever ANY other quantity-aware widget (another card
		// for the same product, or the Cart Items widget) changes the cart.
		document.addEventListener( 'gws-cart-updated', syncFromCart );
	};

	/**
	 * Page-global (not per-`$scope`, unlike GWSInitCardSizer) — a single
	 * card's "+" button click can affect other instances of the same
	 * product elsewhere on the page, so this only ever needs to run once,
	 * on the first gws-product-card instance Elementor initializes. The
	 * `initialized` guard above makes any later hook firings a no-op. Same
	 * `frontend/element_ready` + `elementor/frontend/init` shape as
	 * elementor-manual-card.js/elementor-card-sizer.js — see project
	 * memory "Elementor custom widget JS init pattern".
	 */
	function registerElementReadyHook() {
		window.elementorFrontend.hooks.addAction( 'frontend/element_ready/gws-product-card.default', window.GWSInitProductCardQuantity );
	}

	if ( window.elementorFrontend && window.elementorFrontend.hooks ) {
		registerElementReadyHook();
	} else {
		window.addEventListener( 'elementor/frontend/init', registerElementReadyHook );
	}
} )();
